import React, { useRef, useState, useContext, useEffect } from "react";
import { Box, Button, Flex, Text, Input, useToast, Heading, VStack, Divider, Spinner, Center, Select, IconButton, Tooltip } from "@chakra-ui/react";
import axios from "axios";
import { MdOutlineFileDownload } from "react-icons/md";
import { FaArrowUpFromBracket, FaTrash } from "react-icons/fa6";
import { RiArrowDropDownLine } from "react-icons/ri";
import { ApplicationContext } from "../utils/contextsProviders";
import { saveAs } from "file-saver";

// Helper function to extract company name from location
const extractCompanyFromLocation = (locationName) => {
  const parts = locationName.split('-');
  if (parts.length >= 3) {
    return parts[2].trim();
  }
  return locationName;
};

// Helper function to extract location display name (index 2 after split by '-')
// Format: "0001-P-NRMA Port Macquarie NSW - DB East" → "NRMA Port Macquarie NSW"
const extractLocationDisplayName = (locationName) => {
  if (!locationName || typeof locationName !== 'string') {
    return locationName;
  }
  const parts = locationName.split('-');
  if (parts.length >= 3) {
    return parts[2].trim();
  }
  return locationName;
};

// Helper function to extract location name for geocoding API
// Removes state codes and cleans up the name
const extractLocationNameForGeocoding = (locationDisplayName) => {
  if (!locationDisplayName || typeof locationDisplayName !== 'string') {
    return locationDisplayName;
  }
  
  let cleanedName = locationDisplayName;
  
  // Remove common state codes (Australia) - match whole word only
  const stateCodes = ['NSW', 'QLD', 'VIC', 'SA', 'TAS', 'WA', 'NT', 'ACT'];
  stateCodes.forEach(code => {
    cleanedName = cleanedName.replace(new RegExp(`\\b${code}\\b`, 'gi'), '').trim();
  });
  
  // Remove common Belgian region codes
  const belgianRegions = ['BCR', 'FR', 'WR'];
  belgianRegions.forEach(region => {
    cleanedName = cleanedName.replace(new RegExp(`\\b${region}\\b`, 'gi'), '').trim();
  });
  
  // Remove parts after dashes that look like descriptors (e.g., " - DB East", " - Nadine The Cake Queen")
  // But keep the main location name (everything before the last significant dash)
  // Split by dash and take the first meaningful part
  const parts = cleanedName.split(/\s*-\s*/);
  if (parts.length > 1) {
    // Take the first part, but if it's very short, take first two parts
    cleanedName = parts[0].trim();
    if (cleanedName.length < 5 && parts.length > 1) {
      cleanedName = parts.slice(0, 2).join(' ').trim();
    }
  }
  
  // Clean up extra spaces
  cleanedName = cleanedName.replace(/\s+/g, ' ').trim();
  
  return cleanedName || locationDisplayName; // Fallback to original if cleaning removed everything
};

// Helper function to determine country from location name
const getCountryFromLocation = (locationName) => {
  if (!locationName || typeof locationName !== 'string') {
    return 'Australia';
  }
  
  const upperName = locationName.toUpperCase();
  
  // Check for Australian state codes
  const australianStates = ['NSW', 'QLD', 'VIC', 'SA', 'TAS', 'WA', 'NT', 'ACT'];
  if (australianStates.some(state => upperName.includes(state))) {
    return 'Australia';
  }
  
  // Check for Belgian regions
  const belgianRegions = ['BRUSSELS', 'FLANDERS', 'WALLONIA', 'BCR'];
  if (belgianRegions.some(region => upperName.includes(region))) {
    return 'Belgium';
  }
  
  // Default to Australia
  return 'Australia';
};

// Helper function to format date as YYYY-MM-DD
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to calculate date range based on time period (similar to totalTab.jsx)
const calculateDateRange = (timePeriod) => {
  const today = new Date();
  let startDate, endDate;
  
  switch (timePeriod) {
    case "day":
      const yesterday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1
      );
      startDate = new Date(yesterday);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(yesterday);
      endDate.setHours(23, 59, 59, 999);
      break;
    
    case "week":
      const now = new Date();
      const lastMonday = new Date(now);
      lastMonday.setDate(now.getDate() - ((now.getDay() + 6) % 7) - 7);
      lastMonday.setHours(0, 0, 0, 0);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastMonday.getDate() + 6);
      lastSunday.setHours(23, 59, 59, 999);
      startDate = lastMonday;
      endDate = lastSunday;
      break;
    
    case "month":
      const lastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1
      );
      startDate = lastMonth;
      endDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
        23,
        59,
        59,
        999
      );
      break;
    
    case "quarter":
      const currentQuarter = Math.floor((today.getMonth() + 3) / 3);
      const lastQuarterStartMonth = currentQuarter * 3 - 6;
      const lastQuarterStart = new Date(
        today.getFullYear(),
        lastQuarterStartMonth,
        1
      );
      startDate = lastQuarterStart;
      endDate = new Date(
        today.getFullYear(),
        lastQuarterStartMonth + 3,
        0,
        23,
        59,
        59,
        999
      );
      break;
    
    case "half-year":
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      
      if (currentMonth < 6) {
        startDate = new Date(currentYear - 1, 6, 1);
        endDate = new Date(currentYear - 1, 11, 31, 23, 59, 59, 999);
      } else {
        startDate = new Date(currentYear, 0, 1);
        endDate = new Date(currentYear, 5, 30, 23, 59, 59, 999);
      }
      break;
    
    case "year":
      const lastFiscalYearStart = new Date(
        today.getMonth() >= 6
          ? today.getFullYear() - 1
          : today.getFullYear() - 2,
        6,
        1
      );
      startDate = lastFiscalYearStart;
      endDate = new Date(
        lastFiscalYearStart.getFullYear() + 1,
        5,
        30,
        23,
        59,
        59,
        999
      );
      break;
    
    case "2years":
      const twoYearsStart = new Date(
        today.getMonth() >= 6
          ? today.getFullYear() - 2
          : today.getFullYear() - 3,
        6,
        1
      );
      startDate = twoYearsStart;
      endDate = new Date(
        twoYearsStart.getFullYear() + 2,
        5,
        30,
        23,
        59,
        59,
        999
      );
      break;
    
    default:
      startDate = today;
      endDate = today;
  }
  
  return { startDate, endDate };
};

const AssetRegisterTab = () => {
  const fileInputRef = useRef();
  const companyAssetFileInputRef = useRef();
  const companyEnergyFileInputRef = useRef();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [deleting, setDeleting] = useState({});
  const [companyDownloading, setCompanyDownloading] = useState({});
  const [companyDeleting, setCompanyDeleting] = useState({});
  const [companyUploading, setCompanyUploading] = useState(false);
  const [uploadingCompanyName, setUploadingCompanyName] = useState(null);
  const [uploadingCompanyFileType, setUploadingCompanyFileType] = useState(null);
  const { user } = useContext(ApplicationContext);

  // State for company/location structure
  const [companiesData, setCompaniesData] = useState([]);
  const [uploadingLocation, setUploadingLocation] = useState(null);
  const [uploadingFileType, setUploadingFileType] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch locations and files based on user role
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First, fetch locations
        let userLocations = [];
        
        if (user.role === "SuperAdmin") {
          // For SuperAdmin, fetch all locations from Smappee API
          try {
            const locationResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/smappee/service-locations`
            );
            
            userLocations = locationResponse.data.serviceLocations
              .map(loc => loc.name)
              .filter(name => !name.includes("CABIN") && !name.includes("Elvy"));
          } catch (error) {
            console.error('Error fetching locations:', error);
          }
        } else {
          // For CustomerAdmin and CustomerUser, use their assigned locations
          userLocations = user.locations || [];
        }

        setLocations(userLocations);

        // Now fetch files
        const config = {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        };

        let filesResponse;
        if (user.role === "SuperAdmin") {
          filesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
            config
          );
        } else {
          filesResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
            config
          );
        }

        // Group locations by company - use user.companyName for CustomerAdmin/CustomerUser
        // For SuperAdmin, group by the companyName from files or create separate groups
        const companyMap = {};
        
        if (user.role === "SuperAdmin") {
          // For SuperAdmin, group by companyName from files
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          
          filesData.forEach(companyData => {
            const companyName = companyData.companyName || 'Unknown';
            if (!companyMap[companyName]) {
              companyMap[companyName] = {
                companyName,
                locations: {},
                companyFiles: companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null },
              };
            } else if (companyData.companyFiles) {
              companyMap[companyName].companyFiles = companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null };
            }

            // Add locations from files - group by unique display names
            companyData.locations.forEach(locationData => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (!companyMap[companyName].locations[displayName]) {
                companyMap[companyName].locations[displayName] = {
                  location: locationData.location,
                  locationDisplayName: displayName,
                  files: locationData.files || {}
                };
              } else {
                // Merge files if location already exists
                companyMap[companyName].locations[displayName].files = {
                  ...companyMap[companyName].locations[displayName].files,
                  ...locationData.files
                };
              }
            });
          });

        } else {
          // For CustomerAdmin/CustomerUser, use user.companyName
          // Make sure companyName is not empty or undefined
          let companyName = user.companyName;
          if (!companyName || companyName.trim() === '') {
            // Try to get from files response if available
            const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
            if (filesData.length > 0 && filesData[0].companyName) {
              companyName = filesData[0].companyName;
            } else {
              companyName = 'Unknown';
            }
          }

          if (!companyMap[companyName]) {
            companyMap[companyName] = {
              companyName,
              locations: {},
              companyFiles: filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null },
            };
          } else if (filesResponse.data.companyFiles) {
            companyMap[companyName].companyFiles = filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null };
          }
          
          // Get unique location display names from user locations
          const uniqueLocationDisplayNames = new Set();
          const locationMap = {}; // Map display name to first full location string
          
          userLocations.forEach(locationName => {
            const displayName = extractLocationDisplayName(locationName);
            uniqueLocationDisplayNames.add(displayName);
            if (!locationMap[displayName]) {
              locationMap[displayName] = locationName; // Store first occurrence
            }
          });
          
          // Add unique locations to company map
          uniqueLocationDisplayNames.forEach(displayName => {
            const fullLocationName = locationMap[displayName];
            companyMap[companyName].locations[displayName] = {
              location: fullLocationName, // Use first full location for file operations
              locationDisplayName: displayName,
              files: {}
            };
          });
          
          // Merge with files data
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          
          filesData.forEach(companyData => {
            if (companyData.companyName === companyName) {
              companyData.locations.forEach(locationData => {
                const displayName = extractLocationDisplayName(locationData.location);
                if (companyMap[companyName].locations[displayName]) {
                  companyMap[companyName].locations[displayName].files = locationData.files || {};
                }
              });
            }
          });
        }

        // Convert to array format
        const result = Object.values(companyMap).map(company => ({
          companyName: company.companyName,
          companyFiles: company.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          locations: Object.values(company.locations)
        }));

        setCompaniesData(result);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: "Failed to load data",
          description: error.response?.data?.message || "An error occurred",
          status: "error",
          duration: 3000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user.role, user.locations, toast]);

  // Download handler (slot optional - for Asset Register per-location when multiple files)
  const handleDownload = async (location, fileType, slot) => {
    const downloadKey = slot != null ? `${location}-${fileType}-${slot}` : `${location}-${fileType}`;
    setDownloading(prev => ({ ...prev, [downloadKey]: true }));
    
    try {
      const params = { location, fileType };
      if (slot != null) params.slot = slot;
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        responseType: 'blob',
        params
      };

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/download`,
        config
      );

      // Get filename from Content-Disposition header or use default
      let filename = `${fileType.toLowerCase().replace(' ', '-')}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        status: "success",
        duration: 2000
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Failed to download file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDownloading(prev => ({ ...prev, [downloadKey]: false }));
  };

  // Upload handler
  const handleUpload = async (e, location, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedExtensions = ['.xlsx', '.csv', '.pdf'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: "Only .xlsx, .csv, and .pdf files are allowed",
        status: "error",
        duration: 3000
      });
      e.target.value = null;
      return;
    }

    // Check file size (20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 20MB",
        status: "error",
        duration: 3000
      });
      e.target.value = null;
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("location", location);
    formData.append("fileType", fileType);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/upload`,
        formData,
        config
      );

      toast({
        title: "File uploaded successfully",
        status: "success",
        duration: 2000
      });

      // Refresh data using the same logic as initial fetch
      const fetchConfig = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      };

      let filesResponse;
      if (user.role === "SuperAdmin") {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
          fetchConfig
        );
      } else {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
          fetchConfig
        );
      }

      // Group locations by company - use user.companyName for CustomerAdmin/CustomerUser
      const companyMap = {};
      
        if (user.role === "SuperAdmin") {
          // For SuperAdmin, use files data structure
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];

          filesData.forEach(companyData => {
            const companyName = companyData.companyName || 'Unknown';
            if (!companyMap[companyName]) {
              companyMap[companyName] = {
                companyName,
                locations: {},
                companyFiles: companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null },
              };
            } else if (companyData.companyFiles) {
              companyMap[companyName].companyFiles = companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null };
            }

            // Add locations from files - group by unique display names
            companyData.locations.forEach(locationData => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (!companyMap[companyName].locations[displayName]) {
                companyMap[companyName].locations[displayName] = {
                  location: locationData.location,
                  locationDisplayName: displayName,
                  files: locationData.files || {}
                };
              } else {
                // Merge files if location already exists
                companyMap[companyName].locations[displayName].files = {
                  ...companyMap[companyName].locations[displayName].files,
                  ...locationData.files
                };
              }
            });
          });
      } else {
        // For CustomerAdmin/CustomerUser, use user.companyName
        let companyName = user.companyName;
        if (!companyName || companyName.trim() === '') {
          // Try to get from files response if available
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          if (filesData.length > 0 && filesData[0].companyName) {
            companyName = filesData[0].companyName;
          } else {
            companyName = 'Unknown';
          }
        }

        if (!companyMap[companyName]) {
          companyMap[companyName] = {
            companyName,
            locations: {},
            companyFiles: filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          };
        } else if (filesResponse.data.companyFiles) {
          companyMap[companyName].companyFiles = filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null };
        }

        // Get unique location display names from user locations
        const uniqueLocationDisplayNames = new Set();
        const locationMap = {}; // Map display name to first full location string

        locations.forEach(locationName => {
          const displayName = extractLocationDisplayName(locationName);
          uniqueLocationDisplayNames.add(displayName);
          if (!locationMap[displayName]) {
            locationMap[displayName] = locationName; // Store first occurrence
          }
        });

        // Add unique locations to company map
        uniqueLocationDisplayNames.forEach(displayName => {
          const fullLocationName = locationMap[displayName];
          companyMap[companyName].locations[displayName] = {
            location: fullLocationName, // Use first full location for file operations
            locationDisplayName: displayName,
            files: {}
          };
        });

        // Merge with files data
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];

        filesData.forEach(companyData => {
          if (companyData.companyName === companyName) {
            companyData.locations.forEach(locationData => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (companyMap[companyName].locations[displayName]) {
                companyMap[companyName].locations[displayName].files = locationData.files || {};
              }
            });
          }
        });
      }

      // Convert to array format
      const result = Object.values(companyMap).map(company => ({
        companyName: company.companyName,
        companyFiles: company.companyFiles || { 'Asset Register': null, 'Energy Report': null },
        locations: Object.values(company.locations)
      }));

      setCompaniesData(result);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Failed to upload file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setUploading(false);
    e.target.value = null;
    setUploadingLocation(null);
    setUploadingFileType(null);
  };

  // Company-level download handler
  const handleCompanyDownload = async (companyName, fileType, slot) => {
    const downloadKey = `company-${companyName}-${fileType}-${slot ?? ''}`;
    setCompanyDownloading((prev) => ({ ...prev, [downloadKey]: true }));

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        responseType: 'blob',
        params: { companyName, fileType, slot },
      };

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company/download`,
        config
      );

      let filename = `${(fileType || 'file').toLowerCase().replace(' ', '-')}-company-${companyName}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      console.error('Company download error:', error);
      toast({
        title: "Failed to download company file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000,
      });
    }

    setCompanyDownloading((prev) => ({ ...prev, [downloadKey]: false }));
  };

  // Company-level upload handler
  const handleCompanyUpload = async (e, companyName, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedExtensions = ['.xlsx', '.csv', '.pdf'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: "Only .xlsx, .csv, and .pdf files are allowed",
        status: "error",
        duration: 3000,
      });
      e.target.value = null;
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 20MB",
        status: "error",
        duration: 3000,
      });
      e.target.value = null;
      return;
    }

    setCompanyUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyName", companyName);
    formData.append("fileType", fileType);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company/upload`,
        formData,
        config
      );

      toast({
        title: "Company file uploaded successfully",
        status: "success",
        duration: 2000,
      });

      // Refresh data using same logic as initial fetch
      const fetchConfig = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      };

      let filesResponse;
      if (user.role === "SuperAdmin") {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
          fetchConfig
        );
      } else {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
          fetchConfig
        );
      }

      const companyMap = {};

      if (user.role === "SuperAdmin") {
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];

        filesData.forEach((companyData) => {
          const cName = companyData.companyName || 'Unknown';
          if (!companyMap[cName]) {
            companyMap[cName] = {
              companyName: cName,
              locations: {},
              companyFiles: companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null },
            };
          } else if (companyData.companyFiles) {
            companyMap[cName].companyFiles = companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null };
          }

          companyData.locations.forEach((locationData) => {
            const displayName = extractLocationDisplayName(locationData.location);
            if (!companyMap[cName].locations[displayName]) {
              companyMap[cName].locations[displayName] = {
                location: locationData.location,
                locationDisplayName: displayName,
                files: locationData.files || {},
              };
            } else {
              companyMap[cName].locations[displayName].files = {
                ...companyMap[cName].locations[displayName].files,
                ...locationData.files,
              };
            }
          });
        });
      } else {
        let cName = user.companyName;
        if (!cName || cName.trim() === '') {
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          cName = filesData.length > 0 && filesData[0].companyName ? filesData[0].companyName : 'Unknown';
        }

        if (!companyMap[cName]) {
          companyMap[cName] = {
            companyName: cName,
            locations: {},
            companyFiles: filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          };
        } else if (filesResponse.data.companyFiles) {
          companyMap[cName].companyFiles = filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null };
        }

        const uniqueLocationDisplayNames = new Set();
        const locationMap = {};
        locations.forEach((locationName) => {
          const displayName = extractLocationDisplayName(locationName);
          uniqueLocationDisplayNames.add(displayName);
          if (!locationMap[displayName]) locationMap[displayName] = locationName;
        });
        uniqueLocationDisplayNames.forEach((displayName) => {
          companyMap[cName].locations[displayName] = {
            location: locationMap[displayName],
            locationDisplayName: displayName,
            files: {},
          };
        });

        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
        filesData.forEach((companyData) => {
          if (companyData.companyName === cName) {
            companyData.locations.forEach((locationData) => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (companyMap[cName].locations[displayName]) {
                companyMap[cName].locations[displayName].files = locationData.files || {};
              }
            });
          }
        });
      }

      setCompaniesData(
        Object.values(companyMap).map((company) => ({
          companyName: company.companyName,
          companyFiles: company.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          locations: Object.values(company.locations),
        }))
      );
    } catch (error) {
      console.error('Company upload error:', error);
      toast({
        title: "Failed to upload company file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000,
      });
    }

    setCompanyUploading(false);
    e.target.value = null;
    setUploadingCompanyName(null);
    setUploadingCompanyFileType(null);
  };

  // Delete handler (slot optional - for Asset Register per-location when multiple files)
  const handleDelete = async (location, fileType, slot) => {
    const deleteKey = slot != null ? `${location}-${fileType}-${slot}` : `${location}-${fileType}`;
    setDeleting(prev => ({ ...prev, [deleteKey]: true }));
    
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(slot != null && { params: { slot } })
      };

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/${encodeURIComponent(location)}/${encodeURIComponent(fileType)}`,
        config
      );

      toast({
        title: "File deleted successfully",
        status: "success",
        duration: 2000
      });

      // Refresh data
      const fetchConfig = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      };

      let filesResponse;
      if (user.role === "SuperAdmin") {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
          fetchConfig
        );
      } else {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
          fetchConfig
        );
      }

      const companyMap = {};
      
      if (user.role === "SuperAdmin") {
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
        filesData.forEach(companyData => {
          const companyName = companyData.companyName || 'Unknown';
          if (!companyMap[companyName]) {
            companyMap[companyName] = {
              companyName,
              locations: {},
              companyFiles: companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null },
            };
          } else if (companyData.companyFiles) {
            companyMap[companyName].companyFiles = companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null };
          }
          companyData.locations.forEach(locationData => {
            const displayName = extractLocationDisplayName(locationData.location);
            if (!companyMap[companyName].locations[displayName]) {
              companyMap[companyName].locations[displayName] = {
                location: locationData.location,
                locationDisplayName: displayName,
                files: locationData.files || {}
              };
            } else {
              companyMap[companyName].locations[displayName].files = {
                ...companyMap[companyName].locations[displayName].files,
                ...locationData.files
              };
            }
          });
        });
      } else {
        let companyName = user.companyName;
        if (!companyName || companyName.trim() === '') {
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          companyName = filesData.length > 0 && filesData[0].companyName ? filesData[0].companyName : 'Unknown';
        }
        if (!companyMap[companyName]) {
          companyMap[companyName] = {
            companyName,
            locations: {},
            companyFiles: filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          };
        } else if (filesResponse.data.companyFiles) {
          companyMap[companyName].companyFiles = filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null };
        }
        const uniqueLocationDisplayNames = new Set();
        const locationMap = {};
        locations.forEach(locationName => {
          const displayName = extractLocationDisplayName(locationName);
          uniqueLocationDisplayNames.add(displayName);
          if (!locationMap[displayName]) locationMap[displayName] = locationName;
        });
        uniqueLocationDisplayNames.forEach(displayName => {
          companyMap[companyName].locations[displayName] = {
            location: locationMap[displayName],
            locationDisplayName: displayName,
            files: {}
          };
        });
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
        filesData.forEach(companyData => {
          if (companyData.companyName === companyName) {
            companyData.locations.forEach(locationData => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (companyMap[companyName].locations[displayName]) {
                companyMap[companyName].locations[displayName].files = locationData.files || {};
              }
            });
          }
        });
      }

      setCompaniesData(Object.values(companyMap).map(company => ({
        companyName: company.companyName,
        companyFiles: company.companyFiles || { 'Asset Register': null, 'Energy Report': null },
        locations: Object.values(company.locations)
      })));
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Failed to delete file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000
      });
    }
    setDeleting(prev => ({ ...prev, [deleteKey]: false }));
  };

  // Company-level delete handler
  const handleCompanyDelete = async (companyName, fileType, slot) => {
    const deleteKey = `company-${companyName}-${fileType}-${slot ?? ''}`;
    setCompanyDeleting((prev) => ({ ...prev, [deleteKey]: true }));

    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { companyName, fileType, ...(slot != null && { slot }) },
      };

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/company`,
        config
      );

      toast({
        title: "Company file deleted successfully",
        status: "success",
        duration: 2000,
      });

      // Refresh data
      const fetchConfig = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      };

      let filesResponse;
      if (user.role === "SuperAdmin") {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/by-company`,
          fetchConfig
        );
      } else {
        filesResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asset-register/user-files`,
          fetchConfig
        );
      }

      const companyMap = {};

      if (user.role === "SuperAdmin") {
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
        filesData.forEach((companyData) => {
          const cName = companyData.companyName || 'Unknown';
          if (!companyMap[cName]) {
            companyMap[cName] = {
              companyName: cName,
              locations: {},
              companyFiles: companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null },
            };
          } else if (companyData.companyFiles) {
            companyMap[cName].companyFiles = companyData.companyFiles || { 'Asset Register': null, 'Energy Report': null };
          }
          companyData.locations.forEach((locationData) => {
            const displayName = extractLocationDisplayName(locationData.location);
            if (!companyMap[cName].locations[displayName]) {
              companyMap[cName].locations[displayName] = {
                location: locationData.location,
                locationDisplayName: displayName,
                files: locationData.files || {},
              };
            } else {
              companyMap[cName].locations[displayName].files = {
                ...companyMap[cName].locations[displayName].files,
                ...locationData.files,
              };
            }
          });
        });
      } else {
        let cName = user.companyName;
        if (!cName || cName.trim() === '') {
          const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
          cName = filesData.length > 0 && filesData[0].companyName ? filesData[0].companyName : 'Unknown';
        }
        if (!companyMap[cName]) {
          companyMap[cName] = {
            companyName: cName,
            locations: {},
            companyFiles: filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          };
        } else if (filesResponse.data.companyFiles) {
          companyMap[cName].companyFiles = filesResponse.data.companyFiles || { 'Asset Register': null, 'Energy Report': null };
        }
        const uniqueLocationDisplayNames = new Set();
        const locationMap = {};
        locations.forEach((locationName) => {
          const displayName = extractLocationDisplayName(locationName);
          uniqueLocationDisplayNames.add(displayName);
          if (!locationMap[displayName]) locationMap[displayName] = locationName;
        });
        uniqueLocationDisplayNames.forEach((displayName) => {
          companyMap[cName].locations[displayName] = {
            location: locationMap[displayName],
            locationDisplayName: displayName,
            files: {},
          };
        });
        const filesData = Array.isArray(filesResponse.data) ? filesResponse.data : [filesResponse.data];
        filesData.forEach((companyData) => {
          if (companyData.companyName === cName) {
            companyData.locations.forEach((locationData) => {
              const displayName = extractLocationDisplayName(locationData.location);
              if (companyMap[cName].locations[displayName]) {
                companyMap[cName].locations[displayName].files = locationData.files || {};
              }
            });
          }
        });
      }

      setCompaniesData(
        Object.values(companyMap).map((company) => ({
          companyName: company.companyName,
          companyFiles: company.companyFiles || { 'Asset Register': null, 'Energy Report': null },
          locations: Object.values(company.locations),
        }))
      );
    } catch (error) {
      console.error('Company delete error:', error);
      toast({
        title: "Failed to delete company file",
        description: error.response?.data?.message || "An error occurred",
        status: "error",
        duration: 3000,
      });
    }

    setCompanyDeleting((prev) => ({ ...prev, [deleteKey]: false }));
  };

  // Check if user can upload files
  const canUpload = user.role === "SuperAdmin" || user.role === "CustomerAdmin";

  return (
    <Box p={{ base: 2, md: 6 }}>
      <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }} mb={1}>
        Asset Register / Reports
      </Text>
      <Text mb={4} fontSize="xs" color="gray.400">
        Supported: .xlsx, .csv, .pdf · max 20 MB · Asset Register: up to 2 files · Energy Report: Unlimited
      </Text>

      {!canUpload && (
        <Text mb={4} fontSize="sm" color="orange.500">
          You can only download files. Contact an admin to upload or delete.
        </Text>
      )}

      {loading ? (
        <Center py={10}>
          <VStack spacing={3}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500" fontSize="sm">Loading…</Text>
          </VStack>
        </Center>
      ) : companiesData.length === 0 ? (
        <Text color="gray.400" fontSize="sm">No files found. Upload files to get started.</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {companiesData.map((company, companyIndex) => (
            <Box key={companyIndex} borderWidth="1px" borderColor="gray.300" borderRadius="lg" overflow="hidden" bg="white">

              {/* ── Company header ── */}
              <Flex align="center" justify="space-between" px={4} py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.300">
                <Box minW={0}>
                  <Text fontWeight="bold" fontSize="lg">
                    {company.companyName}
                    {company.locations.length > 0 && (
                      <Text as="span" fontWeight="normal" fontSize="xs" color="gray.500" ml={2}>
                        ({company.locations.map(l => l.locationDisplayName || l.location).join(', ')})
                      </Text>
                    )}
                  </Text>
                </Box>
              </Flex>

              {/* ── Locations ── */}
              {company.locations.length === 0 ? (
                <Text color="gray.400" fontSize="sm" p={4}>No locations found for this company.</Text>
              ) : (
                <VStack spacing={0} align="stretch" divider={<Divider />}>

                  {/* ── Company-level files row ── */}
                  {(() => {
                    const rawCompanyAR = company.companyFiles?.['Asset Register'];
                    const companyAssetFiles = Array.isArray(rawCompanyAR) ? rawCompanyAR : (rawCompanyAR ? [rawCompanyAR] : []);
                    const rawCompanyER = company.companyFiles?.['Energy Report'];
                    const companyEnergyFiles = Array.isArray(rawCompanyER) ? rawCompanyER : (rawCompanyER ? [rawCompanyER] : []);
                    return (
                      <Box px={4} py={3} bg="blue.50">
                        <Text fontWeight="semibold" fontSize="sm" color="blue.700" mb={3}>
                          Company Level
                        </Text>
                        <Flex gap={6} direction={{ base: "column", md: "row" }} align="flex-start">

                          {/* ── Company Asset Register ── */}
                          <Box flex={1} minW={0}>
                            <Flex align="center" justify="space-between" mb={2} gap={2}>
                              <Flex align="center" gap={2} minW={0}>
                                <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                                  Asset Register
                                </Text>
                                {companyAssetFiles.length >= 2 && (
                                  <Text fontSize="xs" color="orange.500" fontStyle="italic">
                                    Limit reached — delete one to upload a new file
                                  </Text>
                                )}
                              </Flex>
                              {canUpload && companyAssetFiles.length < 2 && (
                                <Tooltip label="Upload a company-level Asset Register file" hasArrow>
                                  <Button
                                    leftIcon={<FaArrowUpFromBracket size={10} />}
                                    size="xs"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => { setUploadingCompanyName(company.companyName); setUploadingCompanyFileType('Asset Register'); companyAssetFileInputRef.current?.click(); }}
                                    isLoading={companyUploading && uploadingCompanyName === company.companyName && uploadingCompanyFileType === 'Asset Register'}
                                    loadingText="Uploading"
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                              )}
                            </Flex>
                            {companyAssetFiles.length === 0 ? (
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                            ) : (
                              <VStack spacing={1} align="stretch">
                                {companyAssetFiles.map((file, idx) => {
                                  const slot = file.slot != null ? file.slot : undefined;
                                  const dlKey = `company-${company.companyName}-Asset Register-${slot ?? ''}`;
                                  return (
                                    <Flex key={file._id || idx} align="center" bg="white" borderRadius="md" px={2} py={1.5} gap={2}>
                                      <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                                      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                        {new Date(file.updatedAt).toLocaleDateString()}
                                      </Text>
                                      <Tooltip label="Download" hasArrow>
                                        <IconButton
                                          icon={<MdOutlineFileDownload size={15} />}
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="blue"
                                          aria-label="Download"
                                          onClick={() => handleCompanyDownload(company.companyName, 'Asset Register', slot)}
                                          isLoading={companyDownloading[dlKey]}
                                        />
                                      </Tooltip>
                                      {canUpload && (
                                        <Tooltip label="Delete" hasArrow>
                                          <IconButton
                                            icon={<FaTrash size={11} />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Delete"
                                            onClick={() => handleCompanyDelete(company.companyName, 'Asset Register', slot)}
                                            isLoading={companyDeleting[dlKey]}
                                          />
                                        </Tooltip>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>

                          {/* vertical separator */}
                          <Box w="1px" bg="gray.300" alignSelf="stretch" flexShrink={0} display={{ base: "none", md: "block" }} />

                          {/* ── Company Energy Report ── */}
                          <Box flex={1} minW={0}>
                            <Flex align="center" justify="space-between" mb={2}>
                              <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                                Energy Report
                              </Text>
                              {canUpload && (
                                <Tooltip label="Upload a company-level Energy Report" hasArrow>
                                  <Button
                                    leftIcon={<FaArrowUpFromBracket size={10} />}
                                    size="xs"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => { setUploadingCompanyName(company.companyName); setUploadingCompanyFileType('Energy Report'); companyEnergyFileInputRef.current?.click(); }}
                                    isLoading={companyUploading && uploadingCompanyName === company.companyName && uploadingCompanyFileType === 'Energy Report'}
                                    loadingText="Uploading"
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                              )}
                            </Flex>
                            {companyEnergyFiles.length === 0 ? (
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                            ) : (
                              <VStack spacing={1} align="stretch">
                                {companyEnergyFiles.map((file, idx) => {
                                  const fileSlot = file.slot != null ? file.slot : undefined;
                                  const dlKey = `company-${company.companyName}-Energy Report-${fileSlot ?? ''}`;
                                  return (
                                    <Flex key={file._id || idx} align="center" bg="white" borderRadius="md" px={2} py={1.5} gap={2}>
                                      <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                                      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                        {new Date(file.updatedAt).toLocaleDateString()}
                                      </Text>
                                      <Tooltip label="Download" hasArrow>
                                        <IconButton
                                          icon={<MdOutlineFileDownload size={15} />}
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="blue"
                                          aria-label="Download"
                                          onClick={() => handleCompanyDownload(company.companyName, 'Energy Report', fileSlot)}
                                          isLoading={companyDownloading[dlKey]}
                                        />
                                      </Tooltip>
                                      {canUpload && (
                                        <Tooltip label="Delete" hasArrow>
                                          <IconButton
                                            icon={<FaTrash size={11} />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Delete"
                                            onClick={() => handleCompanyDelete(company.companyName, 'Energy Report', fileSlot)}
                                            isLoading={companyDeleting[dlKey]}
                                          />
                                        </Tooltip>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>

                        </Flex>
                      </Box>
                    );
                  })()}

                  {company.locations.map((locationData, locIndex) => {
                    const locKey = locationData.locationDisplayName || locationData.location;
                    const rawAR = locationData.files?.['Asset Register'];
                    const assetFiles = Array.isArray(rawAR) ? rawAR : (rawAR ? [rawAR] : []);
                    const rawER = locationData.files?.['Energy Report'];
                    const energyFiles = Array.isArray(rawER) ? rawER : (rawER ? [rawER] : []);

                    return (
                      <Box key={locIndex} px={4} py={3}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
                          {locationData.locationDisplayName || locationData.location}
                        </Text>

                        <Flex gap={6} direction={{ base: "column", md: "row" }} align="flex-start">

                          {/* ── Asset Register ── */}
                          <Box flex={1} minW={0}>
                            <Flex align="center" justify="space-between" mb={2} gap={2}>
                              <Flex align="center" gap={2} minW={0}>
                                <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" flexShrink={0}>
                                  Asset Register
                                </Text>
                                {assetFiles.length >= 2 && (
                                  <Text fontSize="xs" color="orange.500" fontStyle="italic">
                                    Limit reached — delete one to upload a new file
                                  </Text>
                                )}
                              </Flex>
                              {canUpload && assetFiles.length < 2 && (
                                <Tooltip label="Upload a new Asset Register file" hasArrow>
                                  <Button
                                    leftIcon={<FaArrowUpFromBracket size={10} />}
                                    size="xs"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => { setUploadingLocation(locKey); setUploadingFileType('Asset Register'); fileInputRef.current?.click(); }}
                                    isLoading={uploading && uploadingLocation === locKey && uploadingFileType === 'Asset Register'}
                                    loadingText="Uploading"
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                              )}
                            </Flex>

                            {assetFiles.length === 0 ? (
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                            ) : (
                              <VStack spacing={1} align="stretch">
                                {assetFiles.map((file, idx) => {
                                  const slot = file?.slot != null && [1, 2].includes(file.slot) ? file.slot : undefined;
                                  const dlKey = slot != null ? `${locKey}-Asset Register-${slot}` : `${locKey}-Asset Register`;
                                  return (
                                    <Flex key={file._id || idx} align="center" bg="gray.50" borderRadius="md" px={2} py={1.5} gap={2}>
                                      <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                                      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                        {new Date(file.updatedAt).toLocaleDateString()}
                                      </Text>
                                      <Tooltip label="Download" hasArrow>
                                        <IconButton
                                          icon={<MdOutlineFileDownload size={15} />}
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="blue"
                                          aria-label="Download"
                                          onClick={() => handleDownload(locKey, 'Asset Register', slot)}
                                          isLoading={downloading[dlKey]}
                                        />
                                      </Tooltip>
                                      {canUpload && (
                                        <Tooltip label="Delete" hasArrow>
                                          <IconButton
                                            icon={<FaTrash size={11} />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Delete"
                                            onClick={() => handleDelete(locKey, 'Asset Register', slot)}
                                            isLoading={deleting[dlKey]}
                                          />
                                        </Tooltip>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>

                          {/* vertical separator */}
                          <Box w="1px" bg="gray.300" alignSelf="stretch" flexShrink={0} display={{ base: "none", md: "block" }} />

                          {/* ── Energy Report ── */}
                          <Box flex={1} minW={0}>
                            <Flex align="center" justify="space-between" mb={2}>
                              <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                                Energy Report
                              </Text>
                              {canUpload && (
                                <Tooltip label="Upload a new Energy Report" hasArrow>
                                  <Button
                                    leftIcon={<FaArrowUpFromBracket size={10} />}
                                    size="xs"
                                    colorScheme="green"
                                    variant="outline"
                                    onClick={() => { setUploadingLocation(locKey); setUploadingFileType('Energy Report'); fileInputRef.current?.click(); }}
                                    isLoading={uploading && uploadingLocation === locKey && uploadingFileType === 'Energy Report'}
                                    loadingText="Uploading"
                                  >
                                    Upload
                                  </Button>
                                </Tooltip>
                              )}
                            </Flex>

                            {energyFiles.length === 0 ? (
                              <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>
                            ) : (
                              <VStack spacing={1} align="stretch">
                                {energyFiles.map((file, idx) => {
                                  const fileSlot = file.slot != null ? file.slot : undefined;
                                  const dlKey = fileSlot != null ? `${locKey}-Energy Report-${fileSlot}` : `${locKey}-Energy Report`;
                                  return (
                                    <Flex key={file._id || idx} align="center" bg="gray.50" borderRadius="md" px={2} py={1.5} gap={2}>
                                      <Text fontSize="xs" flex={1} noOfLines={1} title={file.fileName}>{file.fileName}</Text>
                                      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                        {new Date(file.updatedAt).toLocaleDateString()}
                                      </Text>
                                      <Tooltip label="Download" hasArrow>
                                        <IconButton
                                          icon={<MdOutlineFileDownload size={15} />}
                                          size="xs"
                                          variant="ghost"
                                          colorScheme="blue"
                                          aria-label="Download"
                                          onClick={() => handleDownload(locKey, 'Energy Report', fileSlot)}
                                          isLoading={downloading[dlKey]}
                                        />
                                      </Tooltip>
                                      {canUpload && (
                                        <Tooltip label="Delete" hasArrow>
                                          <IconButton
                                            icon={<FaTrash size={11} />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label="Delete"
                                            onClick={() => handleDelete(locKey, 'Energy Report', fileSlot)}
                                            isLoading={deleting[dlKey]}
                                          />
                                        </Tooltip>
                                      )}
                                    </Flex>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>

                        </Flex>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          ))}
        </VStack>
      )}

      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={fileInputRef}
        display="none"
        onChange={(e) => handleUpload(e, uploadingLocation, uploadingFileType)}
      />
      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={companyAssetFileInputRef}
        display="none"
        onChange={(e) => handleCompanyUpload(e, uploadingCompanyName, 'Asset Register')}
      />
      <Input
        type="file"
        accept=".xlsx,.csv,.pdf"
        ref={companyEnergyFileInputRef}
        display="none"
        onChange={(e) => handleCompanyUpload(e, uploadingCompanyName, 'Energy Report')}
      />
    </Box>
  );
};

export default AssetRegisterTab;
